import { useMemo } from "react";

/**
 * Ensures any entries in `initialValue` that aren't present in `options` still
 * appear in the dropdown — e.g. a saved value whose option was deleted
 * afterwards. Only the form's initial (backend-loaded) value is considered;
 * live user edits do not affect the returned options. After a successful save
 * the parent is expected to re-initialize Formik (React Query invalidation +
 * `enableReinitialize` or remount), at which point the new `initialValue`
 * flows through here.
 */
export function useStickyStaleOptions(
  options: string[],
  initialValue: string | ReadonlyArray<string | number | null> | null | undefined
): string[] {
  return useMemo(() => {
    const initials: string[] =
      initialValue == null
        ? []
        : typeof initialValue === "string"
          ? initialValue
            ? [initialValue]
            : []
          : initialValue.filter((v): v is string => typeof v === "string" && v.length > 0);

    if (initials.length === 0 || options.length === 0) {
      return options;
    }
    const extras = initials.filter((v) => !options.includes(v));
    return extras.length > 0 ? [...options, ...extras] : options;
  }, [options, initialValue]);
}
